import { useState, useEffect } from "react";
import axios from "axios";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Star rating display component
 */
export const StarRating = ({ rating, size = "sm", showNumber = true }) => {
  const starSize = size === "sm" ? "w-4 h-4" : size === "md" ? "w-5 h-5" : "w-6 h-6";
  
  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${starSize} ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "fill-gray-200 text-gray-200"
            }`}
          />
        ))}
      </div>
      {showNumber && rating !== null && (
        <span className="text-sm text-gray-600 ml-1">
          ({rating.toFixed(1)})
        </span>
      )}
    </div>
  );
};

/**
 * Interactive star rating input
 */
export const StarRatingInput = ({ value, onChange }) => {
  const [hoverValue, setHoverValue] = useState(0);

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHoverValue(star)}
          onMouseLeave={() => setHoverValue(0)}
          className="focus:outline-none"
        >
          <Star
            className={`w-8 h-8 transition-colors ${
              star <= (hoverValue || value)
                ? "fill-yellow-400 text-yellow-400"
                : "fill-gray-200 text-gray-200 hover:fill-yellow-200 hover:text-yellow-200"
            }`}
          />
        </button>
      ))}
    </div>
  );
};

/**
 * Seller rating summary with review count
 */
export const SellerRatingSummary = ({ sellerId, className = "" }) => {
  const [rating, setRating] = useState(null);
  const [reviewCount, setReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRating = async () => {
      try {
        const response = await axios.get(`${API}/users/${sellerId}/rating`);
        setRating(response.data.average_rating);
        setReviewCount(response.data.review_count);
      } catch (error) {
        console.error("Error fetching rating:", error);
      } finally {
        setLoading(false);
      }
    };

    if (sellerId) {
      fetchRating();
    }
  }, [sellerId]);

  if (loading || !sellerId) {
    return null;
  }

  if (reviewCount === 0) {
    return (
      <span className={`text-sm text-gray-500 ${className}`}>
        Ingen vurderinger ennå
      </span>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <StarRating rating={rating} size="sm" showNumber={false} />
      <span className="text-sm text-gray-600">
        {rating?.toFixed(1)} ({reviewCount} {reviewCount === 1 ? "vurdering" : "vurderinger"})
      </span>
    </div>
  );
};

/**
 * Review dialog for leaving a review
 */
export const ReviewDialog = ({ sellerId, sellerName, listingId, listingTitle, onReviewSubmitted }) => {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [canReview, setCanReview] = useState(true);
  const [checkingCanReview, setCheckingCanReview] = useState(true);

  useEffect(() => {
    const checkCanReview = async () => {
      if (!isAuthenticated() || !sellerId) {
        setCheckingCanReview(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/reviews/check/${sellerId}`, {
          headers: getAuthHeaders()
        });
        setCanReview(response.data.can_review);
      } catch (error) {
        console.error("Error checking review status:", error);
        setCanReview(false);
      } finally {
        setCheckingCanReview(false);
      }
    };

    checkCanReview();
  }, [sellerId, isAuthenticated, getAuthHeaders]);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Velg en vurdering (1-5 stjerner)");
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/reviews`, {
        seller_id: sellerId,
        listing_id: listingId,
        rating,
        comment: comment.trim() || null
      }, {
        headers: getAuthHeaders()
      });

      toast.success("Vurdering sendt!");
      setOpen(false);
      setRating(0);
      setComment("");
      setCanReview(false);
      
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      const message = error.response?.data?.detail || "Kunne ikke sende vurdering";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Don't show button if not authenticated or can't review
  if (!isAuthenticated() || checkingCanReview) {
    return null;
  }

  if (!canReview) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Star className="w-4 h-4" />
          Gi vurdering
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Vurder selger</DialogTitle>
          <DialogDescription>
            Gi din vurdering av {sellerName || "selgeren"}
            {listingTitle && ` basert på "${listingTitle}"`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Din vurdering
            </label>
            <StarRatingInput value={rating} onChange={setRating} />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Kommentar (valgfritt)
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Fortell andre om din opplevelse med denne selgeren..."
              rows={3}
            />
          </div>
          <Button
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="w-full bg-green-700 hover:bg-green-800"
          >
            {submitting ? "Sender..." : "Send vurdering"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * List of reviews for a seller
 */
export const ReviewList = ({ sellerId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews/seller/${sellerId}`);
        setReviews(response.data);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      } finally {
        setLoading(false);
      }
    };

    if (sellerId) {
      fetchReviews();
    }
  }, [sellerId]);

  if (loading) {
    return <p className="text-gray-500 text-sm">Laster vurderinger...</p>;
  }

  if (reviews.length === 0) {
    return <p className="text-gray-500 text-sm">Ingen vurderinger ennå</p>;
  }

  return (
    <div className="space-y-4">
      {reviews.map((review) => (
        <div key={review.id} className="border-b border-gray-100 pb-4 last:border-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{review.reviewer_name}</span>
              <StarRating rating={review.rating} size="sm" showNumber={false} />
            </div>
            <span className="text-xs text-gray-500">
              {new Date(review.created_at).toLocaleDateString('nb-NO', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
            </span>
          </div>
          {review.comment && (
            <p className="text-gray-600 text-sm">{review.comment}</p>
          )}
          {review.listing_title && (
            <p className="text-xs text-gray-400 mt-1">
              Angående: {review.listing_title}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default {
  StarRating,
  StarRatingInput,
  SellerRatingSummary,
  ReviewDialog,
  ReviewList
};
